import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import {
  useCommunicationActivityDetail,
  type ActivityRow,
} from '@/admin/hooks/use-communications-activity'
import { Button } from '@/shared/components/ui/button'
import { useFormatters } from '@/shared/lib/formatting'

import { ActivitySkipReason, ActivityStatusPill } from './activity-pills'

/**
 * One message in full, expanded in place beneath its row.
 *
 * The body shown is the HTML rendered and stored at send time, so it is what the
 * recipient actually received - not a re-render against today's template. It
 * renders inside a sandboxed iframe (the same approach as ActionEntryDialog)
 * because it is arbitrary tenant-authored HTML.
 *
 * The delivery block reports the send time and the CURRENT state, not a
 * timeline: CommunicationLog carries a single status that the provider webhook
 * advances in place, so per-step timestamps do not exist to show.
 */

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1 text-[11.5px]">
      <span className="w-[104px] shrink-0 text-[var(--ink-3)]">{label}</span>
      <span className="min-w-0 break-words text-[var(--ink)]">{value}</span>
    </div>
  )
}

export function ActivityDetailPanel({
  row,
  onOpenRecord,
  onOpenTemplate,
}: {
  row: ActivityRow
  onOpenRecord?: (targetType: string, targetUuid: string) => void
  onOpenTemplate?: (templateSlug: string) => void
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useFormatters()
  const { data, isPending, isError, refetch } = useCommunicationActivityDetail(
    row.source,
    row.uuid,
  )

  if (isPending) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 text-[12px] text-[var(--ink-3)]">
        <Loader2 size={14} className="animate-spin" />
        {t('comms.detail.loading', { defaultValue: 'Loading message...' })}
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-3 px-4 py-6 text-[12px] text-[var(--ink-2)]">
        {t('comms.detail.error', { defaultValue: "Couldn't load this message." })}
        <Button variant="outline" size="xs" onClick={() => refetch()}>
          {t('common.retry', { defaultValue: 'Retry' })}
        </Button>
      </div>
    )
  }

  const isVoice = data.source === 'voice'

  return (
    <div className="grid gap-0 bg-[var(--bg-card)] lg:grid-cols-[1fr_250px]">
      <div className="min-w-0 p-4">
        {isVoice ? (
          <div className="rounded-md border-[0.5px] border-[var(--rule)] bg-white p-4">
            <p className="mb-3 text-[12.2px] text-[var(--ink-2)]">
              {data.summary ||
                t('comms.detail.noSummary', { defaultValue: 'No call summary.' })}
            </p>
            {data.transcript && (
              <pre className="max-h-[260px] overflow-auto whitespace-pre-wrap font-sans text-[11.8px] leading-relaxed text-[var(--ink-2)]">
                {data.transcript}
              </pre>
            )}
          </div>
        ) : data.body_html ? (
          <iframe
            // Sandboxed: arbitrary tenant-authored HTML must not reach the app's
            // own origin. allow-same-origin is deliberately omitted.
            sandbox=""
            srcDoc={data.body_html}
            title={data.subject || 'Message preview'}
            className="h-[min(58vh,560px)] w-full rounded-md border-[0.5px] border-[var(--rule)] bg-white"
          />
        ) : data.body_text ? (
          <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap rounded-md border-[0.5px] border-[var(--rule)] bg-white p-4 font-sans text-[12.2px] leading-relaxed text-[var(--ink-2)]">
            {data.body_text}
          </pre>
        ) : (
          <div className="rounded-md border-[0.5px] border-dashed border-[var(--rule)] bg-white p-6 text-center text-[12px] text-[var(--ink-3)]">
            {/* Keyed on status, not skip_reason: older skipped rows predate the
              reason taxonomy and have none, and claiming a message "sent as
              normal" when it never sent is the worse error. Delivery state is
              stated separately, so this says nothing about it. */}
            {data.status === 'skipped'
              ? t('comms.detail.noBodySkipped', {
                  defaultValue: 'Nothing was rendered - this message never sent.',
                })
              : t('comms.detail.noBody', {
                  defaultValue: 'The body was not stored for this message.',
                })}
          </div>
        )}

        {data.missing_variables && data.missing_variables.length > 0 && (
          <p className="mt-3 text-[11.5px] text-[var(--status-warn)]">
            {t('comms.detail.missingVariables', {
              defaultValue:
                'The template referenced details this record could not supply, so they rendered blank: {{names}}',
              names: data.missing_variables.join(', '),
            })}
          </p>
        )}
      </div>

      <div className="border-t-[0.5px] border-[var(--rule)] p-4 lg:border-l-[0.5px] lg:border-t-0">
        <p className="mb-3 text-[9.5px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          {t('comms.detail.delivery', { defaultValue: 'Delivery' })}
        </p>

        <Row
          label={t('comms.detail.sent', { defaultValue: 'Sent' })}
          value={formatDateTime(data.occurred_at)}
        />
        {!isVoice && data.status && (
          <div className="flex gap-3 py-1 text-[11.5px]">
            <span className="w-[104px] shrink-0 text-[var(--ink-3)]">
              {t('comms.detail.state', { defaultValue: 'Now' })}
            </span>
            <span className="flex flex-col items-start gap-1">
              <ActivityStatusPill status={data.status} />
              {data.skip_reason_label && (
                <ActivitySkipReason label={data.skip_reason_label} />
              )}
            </span>
          </div>
        )}
        {data.recipient && (
          <Row
            label={t('comms.detail.to', { defaultValue: 'To' })}
            value={
              data.recipient_name
                ? `${data.recipient_name} (${data.recipient})`
                : data.recipient
            }
          />
        )}
        {data.provider_status && (
          <Row
            label={t('comms.detail.provider', { defaultValue: 'Provider' })}
            value={data.provider_status}
          />
        )}
        {/* Platform staff only - the API omits the field entirely for tenant
          admins, so an undefined value means "not yours to see" rather than
          "not captured". Empty string is the meaningful one: the send recorded
          no id, so no tracking event can ever match this row. */}
        {data.provider_message_id !== undefined && (
          <Row
            label={t('comms.detail.providerId', { defaultValue: 'Provider id' })}
            value={
              data.provider_message_id || (
                <span className="text-[var(--ink-3)]">
                  {t('comms.detail.noProviderId', {
                    defaultValue: 'none - cannot be tracked',
                  })}
                </span>
              )
            }
          />
        )}
        {data.error_code && (
          <Row
            label={t('comms.detail.errorCode', { defaultValue: 'Error code' })}
            value={data.error_code}
          />
        )}
        {isVoice && data.duration_ms != null && (
          <Row
            label={t('comms.detail.duration', { defaultValue: 'Duration' })}
            value={`${Math.round(data.duration_ms / 1000)}s`}
          />
        )}
        {data.attachments?.pdf_attached && (
          <Row
            label={t('comms.detail.attachment', { defaultValue: 'Attachment' })}
            value={t('comms.detail.pdfAttached', { defaultValue: 'PDF attached' })}
          />
        )}
        {data.mail_status && (
          <Row
            label={t('comms.detail.mailroom', { defaultValue: 'Mailroom' })}
            value={data.mail_status}
          />
        )}

        <div className="mt-3 flex flex-col items-start gap-1.5 border-t-[0.5px] border-[var(--rule-light)] pt-3">
          {data.attachments?.letter_url && (
            <Button
              variant="link"
              size="xs"
              className="h-auto p-0"
              data-testid="comms-detail-download-letter"
              onClick={() =>
                window.open(data.attachments!.letter_url, '_blank', 'noopener')
              }
            >
              {t('comms.detail.downloadLetter', { defaultValue: 'Download letter' })}
            </Button>
          )}
          {data.target_type && data.target_uuid && onOpenRecord && (
            <Button
              variant="link"
              size="xs"
              className="h-auto p-0"
              onClick={() => onOpenRecord(data.target_type!, data.target_uuid!)}
            >
              {t('comms.detail.openRecord', { defaultValue: 'Open record' })}
            </Button>
          )}
          {data.template_slug && onOpenTemplate && (
            <Button
              variant="link"
              size="xs"
              className="h-auto p-0"
              onClick={() => onOpenTemplate(data.template_slug!)}
            >
              {t('comms.detail.openTemplate', { defaultValue: 'Open template' })}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
